/**
 * DownwardPostureDetector — detect a sustained downward posture with the
 * primary hand below the visible frame (observation only, no intent inferred;
 * distinct from brief, benign downward looks).
 *
 * Behaviour signature:
 *   - Torso turns sideways toward the desk edge
 *   - Head tilts down beyond a normal reading angle
 *   - Primary hand drops below the visible frame
 *   - Sustained ≥ 1.5 s (not a momentary look, e.g. a dropped pen)
 *
 * MediaPipe Pose landmark indices used:
 *   11 LEFT_SHOULDER, 12 RIGHT_SHOULDER
 *   15 LEFT_WRIST,    16 RIGHT_WRIST
 *
 * Signals (each 0..1 strength so Phase 2 can weight them):
 *   s_pitch   = head pitched-down ratio (pitch < -10°: rises 0→1 at -25°)
 *   s_twist   = torso twist via shoulder Z asymmetry
 *   s_shTilt  = shoulder line tilt from horizontal
 *   s_wrist   = primary wrist below visible frame OR low-visibility
 *   s_gaze    = gaze ray endpoint below paper bottom edge
 *
 * Phase-1 verdict: AND of (pitch + (twist OR shTilt) + wrist) sustained.
 * Phase-2/3 verdict ('gated' model, default): the behaviour is
 * conjunctive, not substitutive. The event REQUIRES looking down past normal
 * reading angle (head gate) AND corroborating evidence from at least two
 * of {torso turn, hand concealed below frame, gaze past paper edge}. The
 * score is headGate × (top-2 corroboration), keeping a continuous
 * "activation" meter while preventing one strong channel (e.g. a momentary
 * wrist drop while reaching for the paper) from substituting for the
 * others and crossing threshold — the chief false-positive source of the
 * earlier flat weighted-sum. The legacy weighted sum is retained as the
 * 'linear' model for A/B comparison (?postureModel=linear).
 */

export interface PoseLandmark { x: number; y: number; z?: number; visibility?: number }

export interface DownwardPostureInput {
  poseLandmarks: PoseLandmark[] | null;
  headPitchDeg: number;       // negative = looking down
  gazeEndpointY?: number;      // normalised 0..1 (1 = bottom of frame)
  paperBottomY?: number;       // normalised 0..1, may be undefined if no paper
  now: number;                  // performance.now()
}

export interface DownwardPostureResult {
  /** Phase-1 binary flag — sustained AND signal hit. */
  active: boolean;
  /** Phase-2 continuous score 0..1 — weighted-sum activation meter. */
  score: number;
  /** Seconds the score has been above the activation threshold. */
  sustainedSec: number;
  /** Per-signal strength for HUD inspection. */
  signals: {
    pitch: number;
    twist: number;
    shTilt: number;
    wrist: number;
    gaze: number;
  };
  /** Phase-2: count of distinct events in the rolling 60s window. */
  eventCountWindow: number;
  /** Phase-2: escalation level derived from frequency + severity. */
  escalation: 'none' | 'occasional' | 'frequent';
}

const SUSTAIN_SEC = 1.5;
const TRIGGER_THRESH = 0.55;  // Phase-2 weighted-score threshold
const WEIGHTS = {                // Phase-2 per-signal weight
  pitch:  0.30,
  twist:  0.20,
  shTilt: 0.15,
  wrist:  0.25,
  gaze:   0.10,
};

// Saturating ramp: returns 0 below `lo`, 1 above `hi`, linear in between.
function ramp(v: number, lo: number, hi: number): number {
  if (v <= lo) return 0;
  if (v >= hi) return 1;
  return (v - lo) / (hi - lo);
}

const WINDOW_SEC = 60;   // count events within this rolling window
const EVENT_GAP_SEC = 2;  // a new downPosture requires this gap of inactivity

export type PostureScoreModel = 'gated' | 'linear';

export class DownwardPostureDetector {
  private aboveSince: number | null = null;
  private eventTimestamps: number[] = [];  // when each distinct downPosture triggered
  private wasActive = false;
  private inactiveSince: number | null = null;

  /** @param scoreModel 'gated' (default) = head-gated top-2 corroboration;
   *  'linear' = legacy weighted sum (A/B fallback). */
  constructor(private scoreModel: PostureScoreModel = 'gated') {}

  update(input: DownwardPostureInput): DownwardPostureResult {
    const lm = input.poseLandmarks;

    // s_pitch — head pitched down. Pitch convention: negative = looking
    // down. Begins to count past −10°, saturates at −25°.
    const sPitch = ramp(-input.headPitchDeg, 10, 25);

    let sTwist = 0;
    let sShTilt = 0;
    let sWrist = 0;
    if (lm && lm.length >= 17) {
      const ls = lm[11]; const rs = lm[12];
      const lw = lm[15]; const rw = lm[16];
      const lsVis = ls.visibility ?? 1;
      const rsVis = rs.visibility ?? 1;
      if (lsVis > 0.3 && rsVis > 0.3) {
        // Torso twist — left/right shoulder depth (z) differ when the
        // user rotates the torso around the vertical axis. MediaPipe z
        // is approximate but the sign + magnitude of the difference is
        // reliable for sustained rotation.
        const dz = Math.abs((ls.z ?? 0) - (rs.z ?? 0));
        sTwist = ramp(dz, 0.10, 0.35);

        // Shoulder-line tilt — lean sideways toward the desk edge.
        const dy = (ls.y - rs.y);
        const dx = (ls.x - rs.x) || 1e-6;
        const tiltDeg = Math.abs(Math.atan2(dy, dx) * 180 / Math.PI);
        // 0° = perfectly horizontal; we want deviation
        const dev = Math.min(tiltDeg, 180 - tiltDeg);
        sShTilt = ramp(dev, 8, 25);
      }

      // Wrist below frame — at least one wrist either disappears
      // (visibility low) or moves past the bottom of the frame. Either
      // pattern is the "hand under desk" signal.
      const wristSignal = (w: PoseLandmark) => {
        const vis = w.visibility ?? 0;
        if (vis < 0.3) return 0.8;             // lost / occluded
        if (w.y > 0.85) return ramp(w.y, 0.85, 1.05);
        return 0;
      };
      sWrist = Math.max(wristSignal(lw), wristSignal(rw));
    }

    // s_gaze — gaze ray endpoint below paper bottom.
    let sGaze = 0;
    if (input.gazeEndpointY != null && input.paperBottomY != null) {
      const overshoot = input.gazeEndpointY - input.paperBottomY;
      sGaze = ramp(overshoot, 0.05, 0.30);
    }

    // Legacy weighted sum — substitutive (any channel can pad the total).
    const linearScore =
      WEIGHTS.pitch  * sPitch +
      WEIGHTS.twist  * sTwist +
      WEIGHTS.shTilt * sShTilt +
      WEIGHTS.wrist  * sWrist +
      WEIGHTS.gaze   * sGaze;

    // Gated-conjunctive model (default). headGate is necessary (0 at
    // normal reading pitch, → 1 past −25°); corroboration averages the top
    // two of {posture, hand, gaze}. The equal 0.5/0.5 split caps a single
    // channel at 0.5 — below TRIGGER_THRESH (0.55) — so no lone channel
    // can ever trigger (a stray wrist drop while reading is structurally
    // incapable of firing); ≥2 corroborating channels are required, while
    // losing one channel still works if two remain (partial-loss robust).
    const headGate = sPitch;
    const corrob = [Math.max(sTwist, sShTilt), sWrist, sGaze]
      .sort((a, b) => b - a);
    const gatedScore = headGate * (0.5 * corrob[0] + 0.5 * corrob[1]);

    const score = this.scoreModel === 'linear' ? linearScore : gatedScore;

    const now = input.now;
    if (score >= TRIGGER_THRESH) {
      if (this.aboveSince == null) this.aboveSince = now;
    } else {
      this.aboveSince = null;
    }
    const sustainedSec = this.aboveSince == null
      ? 0 : (now - this.aboveSince) / 1000;
    const active = sustainedSec >= SUSTAIN_SEC;

    // Phase-2: count discrete events within the rolling window. A downPosture
    // transitions from inactive → active and we require EVENT_GAP_SEC of
    // 'idle' between two events so a single sustained downPosture isn't double-
    // counted.
    if (active && !this.wasActive) {
      const last = this.eventTimestamps[this.eventTimestamps.length - 1];
      const longEnoughSinceInactive =
        this.inactiveSince != null
        && (now - this.inactiveSince) / 1000 >= EVENT_GAP_SEC;
      if (last == null || longEnoughSinceInactive) {
        this.eventTimestamps.push(now);
      }
    }
    if (!active) {
      if (this.wasActive) this.inactiveSince = now;
      else if (this.inactiveSince == null) this.inactiveSince = now;
    }
    this.wasActive = active;

    // Prune timestamps older than the window
    const cutoff = now - WINDOW_SEC * 1000;
    while (this.eventTimestamps.length && this.eventTimestamps[0] < cutoff) {
      this.eventTimestamps.shift();
    }
    const eventCountWindow = this.eventTimestamps.length;
    // Escalation: 1 downPosture = suspect, ≥ 3 = strong. Single very long downPosture
    // also escalates (sustained > 4s).
    let escalation: DownwardPostureResult['escalation'] = 'none';
    if (eventCountWindow >= 3 || sustainedSec >= 4) escalation = 'frequent';
    else if (eventCountWindow >= 1 || active) escalation = 'occasional';

    return {
      active, score, sustainedSec, eventCountWindow, escalation,
      signals: { pitch: sPitch, twist: sTwist, shTilt: sShTilt,
                 wrist: sWrist, gaze: sGaze },
    };
  }

  reset(): void {
    this.aboveSince = null;
    this.eventTimestamps = [];
    this.wasActive = false;
    this.inactiveSince = null;
  }
}
